const API_BASE = process.env.REACT_APP_API_URL || '';

async function request(method, path, body, token) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw { status: res.status, ...data };
  return data;
}

async function upload(path, file, token) {
  const form = new FormData();
  form.append('image', file);
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', headers, body: form });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw { status: res.status, ...data };
  return data;
}

const api = {
  // Auth
  login: (email, password) => request('POST', '/api/auth/login', { email, password }),
  register: (body) => request('POST', '/api/auth/register', body),
  me: (token) => request('GET', '/api/auth/me', null, token),
  verifyEmail: (token) => request('GET', `/api/auth/verify-email?token=${encodeURIComponent(token)}`),
  resendVerification: (email) => request('POST', '/api/auth/resend-verification', { email }),
  forgotPassword: (email) => request('POST', '/api/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => request('POST', '/api/auth/reset-password', { token, new_password }),
  deleteAccount: (password, token) => request('POST', '/api/auth/delete-account', { password }, token),

  // Counselors
  counselors: (token, params = '') => request('GET', `/api/counselors${params ? '?' + params : ''}`, null, token),
  counselorDetail: (id, token) => request('GET', `/api/counselors/${id}`, null, token),
  counselorReviews: (id, token) => request('GET', `/api/reviews/counselor/${id}?per_page=100`, null, token),
  counselorApply: (body, token) => request('POST', '/api/counselor/apply', body, token),
  counselorProducts: (id) => request('GET', `/api/counselors/${id}/products`),
  productDetail: (id) => request('GET', `/api/products/${id}`),

  // Product management (counselor)
  myProducts: (token) => request('GET', '/api/counselor/products', null, token),
  createProduct: (body, token) => request('POST', '/api/counselor/products', body, token),
  updateProduct: (id, body, token) => request('PUT', `/api/counselor/products/${id}`, body, token),
  deleteProduct: (id, token) => request('DELETE', `/api/counselor/products/${id}`, null, token),
  uploadProductImage: (file, token) => upload('/api/counselor/products/upload-image', file, token),

  // Chat
  chatRooms: (token) => request('GET', '/api/chat/rooms', null, token),
  chatMessages: (roomId, token, cursor) => {
    const q = cursor ? `?cursor=${cursor}` : '';
    return request('GET', `/api/chat/rooms/${roomId}/messages${q}`, null, token);
  },
  createRoom: (counselorId, token) => request('POST', '/api/chat/rooms', { counselor_id: counselorId }, token),

  // Chat room actions
  blockUser: (roomId, token) => request('POST', `/api/chat/rooms/${roomId}/block`, null, token),
  unblockUser: (roomId, token) => request('POST', `/api/chat/rooms/${roomId}/unblock`, null, token),
  blockStatus: (roomId, token) => request('GET', `/api/chat/rooms/${roomId}/block-status`, null, token),
  muteRoom: (roomId, mute, token) => request('POST', `/api/chat/rooms/${roomId}/mute`, { mute }, token),
  muteStatus: (roomId, token) => request('GET', `/api/chat/rooms/${roomId}/mute-status`, null, token),

  // Voice Room (Discord-style)
  voiceJoin: (roomId, token) => request('POST', '/api/voice/join', { room_id: roomId }, token),
  voiceLeave: (callId, roomId, token) => request('POST', '/api/voice/leave', { call_id: callId, room_id: roomId }, token),
  voiceStatus: (roomId, token) => request('GET', `/api/voice/status/${roomId}`, null, token),

  // Legacy call (backward compat)
  requestCall: (roomId, token) => request('POST', '/api/call/request', { room_id: roomId }, token),
  acceptCall: (callId, token) => request('POST', `/api/call/${callId}/accept`, null, token),
  rejectCall: (callId, token) => request('POST', `/api/call/${callId}/reject`, null, token),
  activeCall: (token) => request('GET', '/api/call/active', null, token),
  endCall: (callId, token) => request('POST', `/api/call/${callId}/end`, null, token),
  callHistory: (token, roomId) => request('GET', `/api/call/history${roomId ? '?room_id=' + roomId : ''}`, null, token),

  // Profile
  profile: (token) => request('GET', '/api/profile/me', null, token),
  updateProfile: (body, token) => request('PUT', '/api/profile/me', body, token),
  uploadImage: (file, token) => upload('/api/profile/upload-image', file, token),

  // Admin
  adminStats: (token) => request('GET', '/api/admin/stats', null, token),
  adminUsers: (token, params = '') => request('GET', `/api/admin/users${params ? '?' + params : ''}`, null, token),
  adminPendingCounselors: (token) => request('GET', '/api/admin/counselors/pending', null, token),
  adminApproveCounselor: (userId, action, token, reason) => request('PUT', `/api/admin/counselors/${userId}/approve`, { action, reason }, token),
  adminReports: (token) => request('GET', '/api/admin/reports', null, token),
  adminResolveReport: (reportId, body, token) => request('PUT', `/api/admin/reports/${reportId}`, body, token),
  adminUpdateRole: (userId, role, token) => request('PUT', `/api/admin/users/${userId}/role`, { role }, token),
  adminToggleCounselor: (userId, grant, token) => request('PUT', `/api/admin/users/${userId}/counselor`, { grant }, token),
  adminBlockUser: (userId, token) => request('PUT', `/api/admin/users/${userId}/block`, null, token),
  adminLogs: (lines, token) => request('GET', `/api/admin/logs?lines=${lines || 100}`, null, token),
  adminSystem: (token) => request('GET', '/api/admin/system', null, token),
  adminChatRooms: (token, page = 1) => request('GET', `/api/admin/chat/rooms?page=${page}`, null, token),
  adminChatMessages: (roomId, token, page = 1) => request('GET', `/api/admin/chat/rooms/${roomId}/messages?page=${page}`, null, token),
  adminConsultations: (token, page = 1) => request('GET', `/api/admin/consultations?page=${page}`, null, token),
  adminRecordingUrl: (callId, token) => request('GET', `/api/admin/recordings/${callId}`, null, token),

  // Support
  startSupportChat: (token) => request('POST', '/api/support/chat', null, token),

  // Notifications
  notifications: (token, page = 1) => request('GET', `/api/notifications?page=${page}`, null, token),
  notificationsMarkRead: (ids, token) => request('POST', '/api/notifications/read', ids ? { ids } : {}, token),
  notificationsUnreadCount: (token) => request('GET', '/api/notifications/unread-count', null, token),

  // Reviews
  createReview: (body, token) => request('POST', '/api/reviews', body, token),
  deleteReview: (reviewId, reason, token) => request('DELETE', `/api/reviews/${reviewId}`, reason ? { reason } : {}, token),
  reportReview: (reviewId, body, token) => request('POST', `/api/reviews/${reviewId}/report`, body, token),

  // Schedule
  mySchedule: (token) => request('GET', '/api/schedule', null, token),
  updateSchedule: (schedules, token) => request('PUT', '/api/schedule', { schedules }, token),
  counselorSchedule: (counselorId) => request('GET', `/api/schedule/counselor/${counselorId}`),
  addScheduleException: (body, token) => request('POST', '/api/schedule/exceptions', body, token),
  getScheduleExceptions: (token) => request('GET', '/api/schedule/exceptions', null, token),

  // Booking
  createBooking: (body, token) => request('POST', '/api/bookings', body, token),
  confirmBooking: (id, token) => request('POST', `/api/bookings/${id}/confirm`, null, token),
  cancelBooking: (id, token) => request('POST', `/api/bookings/${id}/cancel`, null, token),
  myBookings: (token, params = '') => request('GET', `/api/bookings${params ? '?' + params : ''}`, null, token),
  availableSlots: (counselorId, date) => request('GET', `/api/bookings/counselor/${counselorId}/slots?date=${date}`),

  // Dashboard
  counselorStats: (token) => request('GET', '/api/profile/counselor-stats', null, token),

  // Reports with evidence
  createReport: (body, token) => request('POST', '/api/reports', body, token),
  myReports: (token) => request('GET', '/api/reports/mine', null, token),
  blockUserGlobal: (userId, body, token) => request('POST', `/api/blocks/${userId}`, body, token),
  unblockUserGlobal: (userId, token) => request('DELETE', `/api/blocks/${userId}`, null, token),
  myBlocks: (token) => request('GET', '/api/blocks', null, token),
  checkBlock: (userId, token) => request('GET', `/api/blocks/${userId}/check`, null, token),

  // Open Chat
  ocRooms: (token, q) => request('GET', `/api/open-chat/rooms${q ? '?q=' + encodeURIComponent(q) : ''}`, null, token),
  ocMyRooms: (token) => request('GET', '/api/open-chat/rooms?my=1', null, token),
  ocCreateRoom: (body, token) => request('POST', '/api/open-chat/rooms', body, token),
  ocRoomDetail: (id, token) => request('GET', `/api/open-chat/rooms/${id}`, null, token),
  ocJoinRoom: (id, body, token) => request('POST', `/api/open-chat/rooms/${id}/join`, body, token),
  ocLeaveRoom: (id, token) => request('POST', `/api/open-chat/rooms/${id}/leave`, null, token),
  ocMessages: (id, token, cursor) => request('GET', `/api/open-chat/rooms/${id}/messages${cursor ? '?cursor=' + cursor : ''}`, null, token),
  ocKick: (id, uid, token) => request('POST', `/api/open-chat/rooms/${id}/members/${uid}/kick`, null, token),
  ocBan: (id, uid, token) => request('POST', `/api/open-chat/rooms/${id}/members/${uid}/ban`, null, token),
  ocUnban: (id, uid, token) => request('POST', `/api/open-chat/rooms/${id}/members/${uid}/unban`, null, token),
  ocPromote: (id, uid, token) => request('POST', `/api/open-chat/rooms/${id}/members/${uid}/promote`, null, token),
  ocBlock: (id, uid, token) => request('POST', `/api/open-chat/rooms/${id}/block/${uid}`, null, token),
  ocUpdateRoom: (id, body, token) => request('PUT', `/api/open-chat/rooms/${id}`, body, token),
  ocDeleteRoom: (id, token) => request('DELETE', `/api/open-chat/rooms/${id}`, null, token),
  ocCancelDelete: (id, token) => request('POST', `/api/open-chat/rooms/${id}/cancel-delete`, null, token),
  ocTransferOwner: (id, body, token) => request('POST', `/api/open-chat/rooms/${id}/transfer-owner`, body, token),

  // Open Chat — 초대 코드
  ocCreateInviteCode: (id, body, token) => request('POST', `/api/open-chat/rooms/${id}/invite-code`, body, token),
  ocVerifyInvite: (code, token) => request('GET', `/api/open-chat/invite/${code}`, null, token),
  ocJoinByCode: (code, body, token) => request('POST', `/api/open-chat/invite/${code}/join`, body, token),

  // Open Chat — 보이스룸
  ocVoiceStatus: (id, token) => request('GET', `/api/open-chat/rooms/${id}/voice/status`, null, token),
  ocVoiceToken: (id, token) => request('POST', `/api/open-chat/rooms/${id}/voice/token`, null, token),

  // Open Chat — 방별 프로필
  ocUpdateProfile: (id, body, token) => request('PUT', `/api/open-chat/rooms/${id}/profile`, body, token),

  // Open Chat — 공지
  ocAnnouncements: (id, token) => request('GET', `/api/open-chat/rooms/${id}/announcements`, null, token),
  ocCreateAnnouncement: (id, body, token) => request('POST', `/api/open-chat/rooms/${id}/announcements`, body, token),
  ocDeleteAnnouncement: (id, aid, token) => request('DELETE', `/api/open-chat/rooms/${id}/announcements/${aid}`, null, token),
  ocPinAnnouncement: (id, aid, token) => request('POST', `/api/open-chat/rooms/${id}/announcements/${aid}/pin`, null, token),

  // Open Chat — 투표
  ocPolls: (id, token) => request('GET', `/api/open-chat/rooms/${id}/polls`, null, token),
  ocCreatePoll: (id, body, token) => request('POST', `/api/open-chat/rooms/${id}/polls`, body, token),
  ocVote: (id, pid, body, token) => request('POST', `/api/open-chat/rooms/${id}/polls/${pid}/vote`, body, token),
  ocClosePoll: (id, pid, token) => request('POST', `/api/open-chat/rooms/${id}/polls/${pid}/close`, null, token),

  // Open Chat — 일정
  ocEvents: (id, token) => request('GET', `/api/open-chat/rooms/${id}/events`, null, token),
  ocCreateEvent: (id, body, token) => request('POST', `/api/open-chat/rooms/${id}/events`, body, token),
  ocDeleteEvent: (id, eid, token) => request('DELETE', `/api/open-chat/rooms/${id}/events/${eid}`, null, token),

  // Counselor by slug
  counselorBySlug: (slug, token) => request('GET', `/api/counselors/by-slug/${encodeURIComponent(slug)}`, null, token),

  // Blog
  blogPosts: (params = '') => request('GET', `/api/blog/posts${params ? '?' + params : ''}`),
  blogPost: (id) => request('GET', `/api/blog/posts/${id}`),
  blogPostBySlug: (slug) => request('GET', `/api/blog/posts/by-slug/${encodeURIComponent(slug)}`),
  blogMyPosts: (token) => request('GET', '/api/blog/my-posts', null, token),
  blogCreate: (body, token) => request('POST', '/api/blog/posts', body, token),
  blogUpdate: (id, body, token) => request('PUT', `/api/blog/posts/${id}`, body, token),
  blogDelete: (id, token) => request('DELETE', `/api/blog/posts/${id}`, null, token),
  blogLike: (id, token) => request('POST', `/api/blog/posts/${id}/like`, null, token),
  blogComments: (id, page = 1) => request('GET', `/api/blog/posts/${id}/comments?page=${page}`),
  blogAddComment: (id, body, token) => request('POST', `/api/blog/posts/${id}/comments`, body, token),
  blogDeleteComment: (commentId, token) => request('DELETE', `/api/blog/comments/${commentId}`, null, token),
  blogUploadImage: (file, token) => upload('/api/blog/upload-image', file, token),

  // Voice Rooms
  vrList: () => request('GET', '/api/voice-rooms'),
  vrCreate: (body, token) => request('POST', '/api/voice-rooms', body, token),
  vrJoin: (id, token) => request('POST', `/api/voice-rooms/${id}/join`, null, token),
  vrLeave: (id, token) => request('POST', `/api/voice-rooms/${id}/leave`, null, token),
  vrClose: (id, token) => request('DELETE', `/api/voice-rooms/${id}`, null, token),
  vrPromote: (id, body, token) => request('PUT', `/api/voice-rooms/${id}/promote`, body, token),
  vrParticipants: (id) => request('GET', `/api/voice-rooms/${id}/participants`),

  // Radio
  radioLive: () => request('GET', '/api/radio/live'),
  radioDetail: (id, token) => request('GET', `/api/radio/${id}`, null, token),
  radioByRoom: (roomName, token) => request('GET', `/api/radio/by-room/${roomName}`, null, token),
  radioStart: (body, token) => request('POST', '/api/radio/start', body, token),
  radioEnd: (id, token) => request('POST', '/api/radio/end', null, token),
  radioMyBroadcast: (token) => request('GET', '/api/radio/my-broadcast', null, token),
  radioLeave: (id, token) => request('POST', `/api/radio/${id}/leave`, null, token),
  radioComments: (id, params = '') => request('GET', `/api/radio/${id}/comments${params ? '?' + params : ''}`),
  radioComment: (id, body, token) => request('POST', `/api/radio/${id}/comments`, body, token),
  radioAnnouncement: (id, body, token) => request('PUT', `/api/radio/${id}/announcement`, body, token),
  radioDonation: (id, body, token) => request('PUT', `/api/radio/${id}/donation`, body, token),
  radioInvite: (id, body, token) => request('POST', `/api/radio/${id}/invite`, body, token),
  radioAcceptInvite: (id, token) => request('POST', `/api/radio/${id}/accept-invite`, null, token),
  radioRejectInvite: (id, token) => request('POST', `/api/radio/${id}/reject-invite`, null, token),
  radioUninvite: (id, body, token) => request('POST', `/api/radio/${id}/uninvite`, body, token),
  radioRecordings: (token) => request('GET', '/api/radio/recordings', null, token),
  radioDownload: (id, token) => request('GET', `/api/radio/recordings/${id}/download`, null, token),
  radioPast: (params = '') => request('GET', `/api/radio/past${params ? '?' + params : ''}`),
};

export const imgUrl = (path) => {
  if (!path || typeof path !== 'string') return '';

  const raw = path.trim();
  if (!raw) return '';

  // Keep already-resolved URLs untouched.
  if (/^(data:|blob:)/i.test(raw)) return raw;
  if (/^(?:https?:)?\/\//i.test(raw)) return raw;

  if (!API_BASE) return raw;

  const base = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
  return raw.startsWith('/') ? `${base}${raw}` : `${base}/${raw}`;
};
export default api;
